/**
 * Checkout VIP intl unificado: product/price/promo del contrato Stripe
 * (Exclusive Membership → prod_UqbVqDInMZ1S48 / price_1TquIYH8xCanmtKXSrVP1CwE / promo_1TquJs…).
 *
 * Usado por BaseTemplateInternationalDual, MuxVideoPlayerWithCTA (channel_invite intl),
 * y cualquier template con CTA directo a Stripe sin gate Clerk pre-pago.
 */

import {
  INTL_DUAL_CTA_POST_PAYMENT_PATH,
  resolveIntlDualCtaFallbackForVisitor,
  resolveVipStripeCheckoutFromFallback,
} from "@/lib/dualCtaIntlFallback";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import {
  startVipMembershipVideoCheckout,
  type VipMembershipCheckoutGateway,
} from "@/lib/vipMembershipVideoCheckout";

export interface IntlExclusiveMembershipVariant {
  id?: string | null;
  stripePriceId?: string | null;
  /** Promotion Code propio (promo_*) — descuento per-país; gana sobre el del contrato. */
  stripePromotionCodeId?: string | null;
  /** false = sin cupón (toggle admin). */
  stripePromoEnabled?: boolean | null;
  priceUsd?: number | null;
  assignmentScope?: string;
  assignmentGeo?: string;
  mpVariantLabel?: string;
}

export async function launchIntlExclusiveMembershipCheckout(options: {
  countryCode?: string;
  pathname?: string;
  languageForPlans?: string;
  workerTemplate?: string;
  email?: string;
  variant?: IntlExclusiveMembershipVariant | null;
  dualAbBucket?: string;
  /**
   * Se invoca ANTES de location.assign a Stripe (o fan_fallback).
   * Crítico para PostHog: dual debe emitir checkout_opened aquí, no después del await.
   */
  onIntent?: (detail: {
    gateway: VipMembershipCheckoutGateway;
    error?: string;
    code?: string;
  }) => void;
}): Promise<{ gateway: VipMembershipCheckoutGateway }> {
  const intlFallback = resolveIntlDualCtaFallbackForVisitor({
    variantPriceUsd: options.variant?.priceUsd,
    variantStripePriceId: options.variant?.stripePriceId,
  });
  const stripeCheckout = resolveVipStripeCheckoutFromFallback(
    options.variant,
    intlFallback,
  );

  const rawCountry =
    options.countryCode ?? getServerTemplateConfig()?.country ?? "US";
  const pathname =
    options.pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");

  return startVipMembershipVideoCheckout({
    countryCode: rawCountry,
    pathname,
    languageForPlans: options.languageForPlans ?? "en-US",
    workerTemplate:
      options.workerTemplate ??
      getServerTemplateConfig()?.templateName ??
      undefined,
    stripePriceIdOverride: stripeCheckout.stripePriceIdOverride,
    stripePromotionCodeId: stripeCheckout.stripePromotionCodeId,
    variantPriceUsd: options.variant?.priceUsd ?? intlFallback.priceUsd,
    productNameOverride: intlFallback.stripeProductName,
    dualVariantId: stripeCheckout.dualVariantId,
    dualVariantGeoScope: options.variant?.assignmentScope,
    dualVariantGeoCode: options.variant?.assignmentGeo,
    dualAbBucket: options.dualAbBucket,
    variant: options.variant?.mpVariantLabel ?? undefined,
    email: options.email,
    overrideSuccessPath: INTL_DUAL_CTA_POST_PAYMENT_PATH,
    onIntent: options.onIntent,
  });
}
